import { useEffect, useState, useCallback } from "react";
import Head from "next/head";
import Image from "next/image";
import { createClient } from "next-sanity";
import imageUrlBuilder from "@sanity/image-url";
import { getImageDimensions } from "@sanity/asset-utils";
import { useRouter } from "next/router";
import { debounce } from "lodash";

import Typography from "@mui/material/Typography";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import { TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem } from "@mui/material";

import MainHeader from "../../components/MainHeader";
import MainFooter from "../../components/MainFooter";
import HeroIndex from "../../components/HeroIndex";
import Label from "../../components/Label";
import BlogThumb from "../../components/BlogThumb";

import fonts from "../../styles/FontInter.module.css";
import mainTheme from "../../components/mainTheme";
import styles from "../../styles/Blog.module.css";

import * as dayjs from "dayjs";

import client from "../../components/client";
import ExitModal from "../../components/ExitModal";
import FormMini from "../../components/forms/FormMini";

export default function Blog({ data }) {
  const [catFilter, setCatFilter] = useState(data.category);
  const [keywordFilter, setKeywordFilter] = useState(data.keyword);
  const [page, setPage] = useState(data.page);
  const [pageCount, setPageCount] = useState(0);
  const [postCount, setPostCount] = useState(0);
  const [renderPosts, setRenderPosts] = useState(data.renderPosts);
  const [displayPosts, setDisplayPosts] = useState([]);
  const [firstLoadComplete, setFirstLoad] = useState(false);

  const updateDisplayPosts = (possiblePosts) => {
    let length = 12;
    let myPosts = [];
    let thispostCount = 0;
    let start = (page - 1) * length;
    let end = start + length;
    for (let i = 0; i < possiblePosts.length; i++) {
      thispostCount++;
      if (i >= start && i < end) {
        myPosts.push(possiblePosts[i]);
      }
    }
    let thispageCount = Math.ceil(thispostCount / length);
    if (thispostCount > 0 && thispageCount < page) {
      setPage(1);
    }
    setPostCount(thispostCount);
    setDisplayPosts(myPosts);
    setPageCount(thispageCount);
  };

  if (!firstLoadComplete) {
    let posts = filterByCat(data.allPosts, catFilter);
    posts = filterByKeyword(posts, keywordFilter);
    setRenderPosts(posts);
    updateDisplayPosts(posts);
    setFirstLoad(true);
  }

  // Build cat list
  const catList = buildCatList(data.allPosts);

  useEffect(() => {
    dataLayer.push({ event: "optimize.activate" });
  }, []);

  const handleFilter = (event, newValue) => {
    let posts = filterByCat(data.allPosts, newValue.props.value);
    posts = filterByKeyword(posts, keywordFilter);
    setCatFilter(newValue.props.value);
    setRenderPosts(posts);
    updateDisplayPosts(posts);
  };

  const handleKeyword = (event) => {
    let posts = filterByCat(data.allPosts, catFilter);
    posts = filterByKeyword(posts, event.target.value.trim());
    setKeywordFilter(event.target.value.trim());
    setRenderPosts(posts);
    updateDisplayPosts(posts);
  };

  let baseDomain = "https://calm-eclair-926de0.netlify.app/";
  if (process.env.URL) {
    baseDomain = process.env.URL + "/";
  }
  let thumbnailSrc = baseDomain + "og_images/blog.png";

  let filters = [];
  if (catFilter != "") {
    filters.push("c=" + encodeURIComponent(catFilter));
  }
  if (keywordFilter != "") {
    filters.push("k=" + encodeURIComponent(keywordFilter));
  }

  const router = useRouter();

  return (
    <ThemeProvider theme={mainTheme}>
      <Head>
        <title>Elm Street Blog</title>
        <meta
          name="description"
          content="Case Studies, Company News, Customer Success, Product Updates, Eduction, Best Practices, Tech and Industry Webinars"
        />
        <link rel="icon" href="/favicon.ico" />
        <meta property="og:title" content="Elm Street Blog" />
        <meta property="og:type" content="article" />
        <meta
          property="og:description"
          content="Case Studies, Company News, Customer Success, Product Updates, Eduction, Best Practices, Tech and Industry Webinars"
        />
        <meta property="og:image" content={thumbnailSrc} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Elm Street Blog" />
        <meta
          property="twitter:description"
          content="Case Studies, Company News, Customer Success, Product Updates, Eduction, Best Practices, Tech and Industry Webinars"
        />
        <meta name="twitter:image" content={thumbnailSrc} />
      </Head>
      <CssBaseline className={fonts} />
      <MainHeader />

      {catFilter == "Company News" && (
        <ExitModal>
          <div
            style={{
              backgroundImage: "url('orangebg.webp')",
              backgroundPosition: "center",
              backgroundSize: "fixed",
              padding: "30px",
              display: "block",
            }}
          >
            <div style={{ textAlign: "center", paddingBottom: "20px" }}>
              {" "}
              <Typography
                variant="h3"
                component="h2"
                style={{ color: "#ffffff", textAlign: "center", paddingBottom: "10px" }}
              >
                Stay updated...
              </Typography>
              <Typography variant="h5" component="h2" style={{ color: "#333666", textAlign: "center" }}>
                with our free educational content!
              </Typography>
              <br />
              <strong>
                + Webinars
                <br />
                + Helpful Guides
                <br />
                + Marketing Tips
                <br />
                + AND MORE!
                <br />
              </strong>
            </div>
            <div style={{ backgroundColor: "#ffffff", padding: "25px" }}>
              <FormMini
                buttonText="Register Today For Updates"
                listId="147998"
                formName="es2_monday_mentor"
                utmOverride="exit_intent"
              />
            </div>
          </div>
        </ExitModal>
      )}

      <div
        style={{
          backgroundImage: "url('horizonbg.webp')",
          backgroundPosition: "center",
          backgroundSize: "cover",
          height: "500px",
        }}
      >
        <Container maxWidth="lg">
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={6} md={6} style={{ textAlign: "center", paddingTop: "48px", color: "#FFFFFF" }}>
              <Typography variant="h2" component="h1">
                Follow <span style={{ color: mainTheme.palette.secondary.main }}>Our</span> Story.
              </Typography>
              <p>Great stories happen to those that can tell them.</p>
            </Grid>
          </Grid>
        </Container>
      </div>
      <Container maxWidth="lg" style={{ marginTop: "-300px" }}>
        <Grid container columnSpacing={0} style={{ minHeight: "400px" }}>
          <Grid item xs={12} sm={12} md={5}>
            <div style={{ backgroundColor: mainTheme.palette.secondary.main, padding: "32px", minHeight: "376px" }}>
              FEATURED ARTICLE
              <div>
                <Typography variant="h5" component="h2">
                  Don’t be a Secret Agent: 4 Must-Have Strategies for Growing Your Real Estate Brand
                </Typography>
                <div style={{ marginTop: "12px", marginBottom: "24px" }}>
                  For real estate professionals, marketing and branding play important roles in how you set yourself
                  apart from your competition and greatly influence how you create a valuable...
                </div>
              </div>
              <Button
                href="/blog/don-t-be-a-secret-agent-4-must-have-strategies-for-growing-your-real-estate-brand"
                variant="contained"
                color="info"
                style={{ color: "#F69C26" }}
              >
                Read Full Article
              </Button>
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={7}>
            <div
              style={{
                backgroundColor: "#FFFFFF",
                width: "100%",
                minHeight: "380px",
                position: "relative",
                maxWidth: "95vw",
              }}
            >
              <Link href="/blog/don-t-be-a-secret-agent-4-must-have-strategies-for-growing-your-real-estate-brand">
                <Image
                  src="/secret-agent.jpg"
                  width="680px"
                  height="380px"
                  alt="Blog Article - Don’t be a Secret Agent: 4 Must-Have Strategies for Growing Your Real Estate Brand"
                />
              </Link>
            </div>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg" suppressHydrationWarning={true}>
        <Grid container columnSpacing={2}>
          <Grid item md={8} xs={12} style={{ marginTop: "32px" }}>
            <TextField
              label={"Search The Blog"}
              id="keyword_search"
              hiddenLabel
              fullWidth
              size="small"
              onKeyUp={handleKeyword}
              defaultValue={keywordFilter}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Image src="/icon_search.svg" width="20px" height="20px" alt="Keyword Search Icon" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item md={4} xs={12} style={{ marginTop: "32px" }}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-standard-label">Select a Category</InputLabel>
              <Select
                id="category_select"
                label={"Select a Category"}
                onChange={handleFilter}
                value={catFilter}
                fullWidth
                size="small"
              >
                <MenuItem value="">All Categories</MenuItem>
                {catList.map(function (cat) {
                  return (
                    <MenuItem value={cat} key={cat}>
                      {cat}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Container>

      <Container maxWidth="lg">
        <div style={{ marginTop: "48px", marginBottom: "48px" }}>
          <Grid container columnSpacing={8}>
            {!router.isFallback &&
              displayPosts &&
              displayPosts.map(function (post) {
                return (
                  <Grid item xs={12} sm={6} md={4} key={`grid${post._id}`} style={{ marginBottom: "32px" }}>
                    <BlogThumb post={post} client={client} key={post._id} />
                  </Grid>
                );
              })}
            {displayPosts.length === 0 && (
              <Grid item xs={12} sm={6} md={4}>
                No articles found. Please try a different search.
              </Grid>
            )}
            {router.isFallback && (
              <>
                <Grid item xs={12} sm={6} md={4}>
                  Loading blog...
                </Grid>
              </>
            )}
          </Grid>
          <Grid container justifyContent="center" style={{ marginTop: "4px" }}>
            <Grid item xs={12} sm={8} md={6}>
              <div className={styles.paginationContainer} style={{ backgroundColor: mainTheme.palette.primary.dark }}>
                <Pagination
                  count={pageCount}
                  shape="rounded"
                  color="secondary"
                  page={page}
                  size="small"
                  renderItem={(item) => (
                    <PaginationItem component={Link} href={`/blog?p=${item.page}&${filters.join("&")}`} {...item} />
                  )}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </Container>

      <MainFooter />
    </ThemeProvider>
  );
}

export async function getServerSideProps(context) {
  const postQuery = `
    *[_type == "post" ] | order(publishedAt desc){
      _id,
      title,
      slug,
      mainImage {
        asset -> {
          _id,
          url
        }
      },
      publishedAt,
      body,
      thumbnail,
      categories[]->{
        title
      },
      tags[]->{
        tag
      }
    }`;

  const allPosts = await client.fetch(postQuery);

  let page = 1;
  let category = "";
  let keyword = "";
  let errors = "";
  let renderPosts = [];
  if (context.query && context.query.p) {
    page = parseInt(context.query.p);
  }
  if (context.query && context.query.c) {
    category = context.query.c;
  }
  if (context.query && context.query.k) {
    keyword = context.query.k;
  }

  renderPosts = allPosts;
  /*
  if(category.length > 0){
    renderPosts = filterByCat(renderPosts, category);
  }
  try{
    if(keyword.length > 0){
      renderPosts = filterByKeyword(renderPosts, keyword);
    }
  }
  catch(e){
    errors = e;
  }
  */

  return {
    props: {
      data: {
        allPosts,
        renderPosts,
        page,
        category,
        keyword,
        errors,
      },
    },
  };
}

function buildCatList(posts) {
  const catList = [];
  for (let post of posts) {
    if (post.categories) {
      for (let category of post.categories) {
        if (!catList.includes(category.title)) {
          catList.push(category.title);
        }
      }
    }
  }

  catList.sort();

  return catList;
}

function filterByCat(posts, catFilter) {
  if (!catFilter || catFilter.length === 0) {
    return posts;
  }
  let filteredPosts = [];
  for (let post of posts) {
    if (post.categories) {
      for (let category of post.categories) {
        if (category.title == catFilter) {
          filteredPosts.push(post);
        }
      }
    }
  }

  return filteredPosts;
}

function filterByKeyword(posts, keywordFilter) {
  try {
    const regex = /([^a-zA-Z0-9_#. \-])+/g;
    if (posts && posts.length > 0 && keywordFilter && keywordFilter.length >= 3) {
      keywordFilter = keywordFilter.trim().replaceAll(regex, "");
    } else {
      return posts;
    }
  } catch (e) {
    console.log(e);
  }

  if (keywordFilter && keywordFilter.length >= 3) {
    keywordFilter = keywordFilter.toLowerCase();
    let filteredPosts = [];
    let filteredIds = [];
    for (let post of posts) {
      if (post.tags) {
        for (let post_tag of post.tags) {
          if (
            post_tag.tag &&
            post_tag.tag.toLowerCase().search(keywordFilter) !== -1 &&
            !filteredIds.includes(post._id)
          ) {
            filteredPosts.push(post);
            filteredIds.push(post._id);
          }
        }
      }

      if (post.categories) {
        for (let cat of post.categories) {
          if (cat.title && cat.title.toLowerCase().search(keywordFilter) !== -1 && !filteredIds.includes(post._id)) {
            filteredPosts.push(post);
            filteredIds.push(post._id);
          }
        }
      }

      if (post.title && post.title.toLowerCase().search(keywordFilter) !== -1 && !filteredIds.includes(post._id)) {
        filteredPosts.push(post);
        filteredIds.push(post._id);
      }
    }

    return filteredPosts;
  } else {
    return posts;
  }
}
